import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { db, storage } from '../../firebase/config';
import { doc, getDoc, updateDoc, arrayUnion, collection, addDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { CheckCircle2, UploadCloud, User, Save } from 'lucide-react';
import imageCompression from 'browser-image-compression';
import { notifySuccess, notifyError } from '../../utils/alerts';
import { enviarPruebaWhatsApp } from '../../services/twilioTest';

const PagoComprobante = () => {
    const { uid } = useParams();
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [nino, setNino] = useState(null);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [completado, setCompletado] = useState(false);

    // 1. Cargar info del niño por UID
    useEffect(() => {
        const fetchNino = async () => {
            try {
                const docRef = doc(db, "ninos", uid);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setNino(docSnap.data());
                } else {
                    notifyError("Error", "No se encontró el registro.");
                }
            } catch (error) {
                console.log(error)
                notifyError("Error", "Problema de conexión.");
            } finally {
                setLoading(false);
            }
        };
        fetchNino();
    }, [uid]);

    // 2. Lógica de Compresión
    const handleFileChange = async (e) => {
        const imageFile = e.target.files[0];
        if (!imageFile) return;

        const options = {
            maxSizeMB: 0.1,
            maxWidthOrHeight: 1024,
            useWebWorker: true
        };

        try {
            const compressedFile = await imageCompression(imageFile, options);
            setFile(compressedFile);
            setPreview(URL.createObjectURL(compressedFile));
        } catch (error) {
            notifyError("Error", "No se pudo procesar la imagen.");
        }
    };

    // 3. Enviar a Firebase (Storage + Doble registro en Firestore)
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file) return notifyError("Atención", "Adjunta una foto del comprobante.");

        setSending(true);
        try {
            // A. Subir imagen a Storage
            const storageRef = ref(storage, `comprobantes/${uid}_${Date.now()}.jpg`);
            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);

            const fechaActual = new Date().toISOString();

            // Extraemos datos del primer acudiente para la metadata
            const acudientePrincipal = nino.acudientes?.[0] || { nombre: 'N/A', telefono: 'N/A' };

            // B. CREAR REGISTRO EN NUEVA COLECCIÓN "pagos_recibidos"
            await addDoc(collection(db, "pagos_recibidos"), {
                ninoId: uid,
                ninoNombre: nino.nombre,
                acudienteNombre: acudientePrincipal.nombre,
                acudienteTelefono: acudientePrincipal.telefono,
                comprobanteUrl: url,
                fechaEnvio: fechaActual,
                estado: 'Pendiente',
                monto: Number(nino.valorMensualidad)
            });

            // enviar mensaje 
            const payload = {
                templateId: 'HX815f2facd93853a8639e9d7efdfdcd97',
                usuarios: [ // Ahora es un ARRAY de objetos
                    {
                        telefono: `57${acudientePrincipal.telefono}`, // Cambiado de numeroDestino a telefono
                        variables: {
                            "1": acudientePrincipal.nombre,
                            "2": nino.nombre,
                            "3": `$${Number(nino.valorMensualidad).toLocaleString('es-CO')}`,
                            "4": new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })

                        }
                    }
                ]
            };
            enviarPruebaWhatsApp(payload);



            setCompletado(true);
            notifySuccess("¡Enviado!", "Comprobante recibido correctamente.");
        } catch (error) {
            console.error(error);
            notifyError("Error", "No se pudo procesar el pago.");
        } finally {
            setSending(false);
        }
    };

    if (loading) return <div className="flex justify-center p-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;

    if (completado) {
        return (
            <div className="max-w-md mx-auto mt-10 p-8 bg-white rounded-[3rem] shadow-2xl text-center animate-in zoom-in-95">
                <CheckCircle2 size={80} className="text-green-500 mx-auto mb-6" />
                <h2 className="text-2xl font-black text-gray-800 mb-4">¡Muchas gracias por compartir tu comprobante!</h2>
                <p className="text-gray-600 font-medium leading-relaxed">
                    Nuestro equipo lo revisará pronto. Cuando sea aprobado, recibirás tu factura a través de WhatsApp.
                </p>
            </div>
        );
    }

    return (
        <div className="max-w-md mx-auto p-4 pb-20">
            <header className="text-center mb-8">
                <h1 className="text-3xl font-black text-gray-800 tracking-tight">Reportar Pago</h1>
                <p className="text-gray-500 font-bold uppercase tracking-widest text-[10px] mt-2 italic">Jardín Infantil Campestre Moraleja.</p>
            </header>

            <div className="bg-white rounded-[2.5rem] shadow-xl border border-gray-50 overflow-hidden">
                <div className="bg-blue-600 p-8 text-white">
                    <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-md">
                            <User size={24} />
                        </div>
                        <div>
                            <p className="text-xs text-blue-100 font-bold uppercase">Estudiante</p>
                            <h2 className="text-xl font-black">{nino?.nombre}</h2>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white/10 p-3 rounded-2xl">
                            <p className="text-[10px] uppercase font-bold text-blue-100">Grado</p>
                            <p className="text-lg font-black">{nino?.grado}°</p>
                        </div>
                        <div className="bg-white/10 p-3 rounded-2xl border border-white/20">
                            <p className="text-[10px] uppercase font-bold text-blue-100">Valor a Pagar</p>
                            <p className="text-lg font-black">${nino.valorMensualidad.toLocaleString('es-CO')}</p>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="p-8 space-y-6">
                    <div className="space-y-4">
                        <label className="block text-center cursor-pointer group">
                            <div className={`border-4 border-dashed rounded-[2rem] p-8 transition-all ${preview ? 'border-green-400 bg-green-50' : 'border-gray-100 hover:border-blue-200 bg-gray-50'}`}>
                                {preview ? (
                                    <div className="relative inline-block">
                                        <img src={preview} alt="Preview" className="h-40 w-40 object-cover rounded-2xl mx-auto shadow-lg" />
                                        <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full p-1"><CheckCircle2 size={16} /></div>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center">
                                        <UploadCloud size={48} className="text-blue-500 mb-2 group-hover:scale-110 transition-transform" />
                                        <p className="text-sm font-black text-gray-700">Tocar para subir foto</p>
                                        <p className="text-xs text-gray-400 mt-1 font-medium">Comprobante o transferencia</p>
                                    </div>
                                )}
                                <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                            </div>
                        </label>
                    </div>

                    <button
                        type="submit"
                        disabled={sending || !file}
                        className={`w-full p-5 rounded-2xl font-black flex items-center justify-center gap-3 transition-all shadow-xl ${!file ? 'bg-gray-200 text-gray-400' : 'bg-blue-600 text-white shadow-blue-200 hover:bg-blue-700 active:scale-95'}`}
                    >
                        {sending ? (
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                        ) : (
                            <>
                                <Save size={22} />
                                Enviar Comprobante
                            </>
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default PagoComprobante;